import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { type ClientReadableStream, RpcError, StatusCode } from 'grpc-web'
import { Decimal } from '@taxfyle/api-internal/shared/types/decimal_pb'
import { Observable } from 'rxjs/internal/Observable'
import { TimeRange } from '@/infra/time-range'
import { firstValueFrom, timeout, TimeoutError } from 'rxjs'
import { safeParseDate, unixEpoch } from '@/utils/date-util'
import { retry, type RetryOptions } from 'fejl'
import { resolveOnAbort, throwIfAborted } from '@/utils/abort-util'

export interface IDecimal {
  getUnits(): number
  getNanos(): number
}

export interface IDecimalAsObject {
  units: number
  nanos: number
}

export interface IDuration {
  getSeconds(): number
  getNanos(): number
}

export interface IDurationAsObject {
  seconds: number
  nanos: number
}

export interface IInterval {
  getStartTime(): Timestamp | undefined
  getEndTime(): Timestamp | undefined
}

export interface IIntervalAsObject {
  startTime: Timestamp | undefined
  endTime: Timestamp | undefined
}

const nanoFactor = 1_000_000_000

/**
 * Converts a proto Timestamp to an ISO8601 string date
 *
 * @param timestamp The timestamp to convert either in Timestamp protobuf
 * format or as an object
 */
export function timestampToISO(timestamp?: Timestamp | Timestamp.AsObject) {
  if (!timestamp) {
    return timestamp
  }

  if ('toDate' in timestamp) {
    return timestamp.toDate().toISOString()
  } else {
    return new Date(
      timestamp.seconds * 1000 + timestamp.nanos / 1000000,
    ).toISOString()
  }
}

/**
 * Converts a proto Timestamp to a Date
 *
 * @param timestamp The timestamp to convert either in Timestamp protobuf
 * format or as an object
 */
export function timestampToDate(timestamp?: Timestamp | Timestamp.AsObject) {
  const iso = timestampToISO(timestamp)
  return iso ? safeParseDate(iso) : undefined
}

/**
 * Converts a proto Timestamp to a Date, or returns the unix epoch if
 * unable to parse.
 *
 * @param timestamp
 */
export function safeTimestampToDate(
  timestamp?: Timestamp | Timestamp.AsObject,
) {
  return timestampToDate(timestamp) ?? unixEpoch()
}

/**
 * Creates a timestamp from an ISO representation.
 *
 * @param iso
 * @constructor
 */
export function ISOToTimestamp(iso: string) {
  const timestamp = new Timestamp()
  timestamp.fromDate(new Date(iso))
  return timestamp
}

/**
 * Converts a proto custom Decimal type into a Number
 *
 * @param decimal The decimal to convert either in Decimal protobuf format or as
 * an object
 */
export function decimalToNumber(decimal: IDecimal | IDecimalAsObject) {
  if ('getUnits' in decimal) {
    return decimal.getUnits() + decimal.getNanos() / nanoFactor
  } else {
    return decimal.units + decimal.nanos / nanoFactor
  }
}

/**
 * Converts a proto custom Decimal type into a Number, or returns undefined if
 * the input is undefined.
 *
 * @param decimal
 */
export function maybeDecimalToNumber(
  decimal: IDecimal | IDecimalAsObject | undefined,
) {
  if (decimal) {
    return decimalToNumber(decimal)
  } else {
    return undefined
  }
}

/**
 * Converts a proto custom Decimal type into a Number, or returns zero
 * if the input is undefined.
 *
 * @param decimal
 */
export function safeDecimalToNumber(
  decimal: IDecimal | IDecimalAsObject | undefined,
) {
  return maybeDecimalToNumber(decimal) ?? 0
}

/**
 * Converts a Number into a proto custom Decimal type
 *
 * @param number The number to convert to a decimal.
 */
export function numberToDecimal(number: number): Decimal {
  const decimal = new Decimal()
  const units = Math.floor(number)

  // IEEE 754 floating-point precision fix:
  // Even "exact" decimal numbers like 420.69 cannot be stored precisely in binary.
  // JavaScript actually stores 420.69 as ~420.689999999999997726, which is slightly
  // less than the intended value. When we extract the fractional part and multiply
  // by nanoFactor (1e9), we get ~689999999.999997735 instead of 690000000.
  // Math.floor() would truncate this to 689999999, resulting in 420.689999999
  // instead of the expected 420.69. Math.round() compensates for this IEEE 754
  // imprecision by rounding 689999999.999997735 to the correct 690000000.
  const nanos = Math.round((number - units) * nanoFactor)

  decimal.setUnits(units)
  decimal.setNanos(nanos)
  return decimal
}

/**
 * Converts a proto duration into minutes.
 *
 * @param duration The duration to convert either in Duration protobuf format or as an object
 * @returns The duration in minutes
 */
export function durationToMinutes(
  duration: IDuration | IDurationAsObject,
): number {
  if ('getSeconds' in duration) {
    return (duration.getSeconds() + duration.getNanos() / nanoFactor) / 60
  } else {
    return (duration.seconds + duration.nanos / nanoFactor) / 60
  }
}

/**
 * Converts a proto interval to a TimeRange.
 *
 * @param interval The interval to convert either in Interval protobuf format or as an object
 * @returns The interval as a TimeRange or undefined if the interval is invalid
 */
export function maybeParseIntervalToTimeRange(
  interval: IInterval | IIntervalAsObject | undefined,
): TimeRange | undefined {
  if (!interval) {
    return undefined
  }

  let startTime: string | undefined
  let endTime: string | undefined

  if ('getStartTime' in interval && 'getEndTime' in interval) {
    startTime = timestampToISO(interval.getStartTime())
    endTime = timestampToISO(interval.getEndTime())
  } else {
    startTime = timestampToISO(interval.startTime)
    endTime = timestampToISO(interval.endTime)
  }

  if (startTime === undefined || endTime === undefined) {
    return undefined
  }

  return TimeRange.of(new Date(startTime), new Date(endTime))
}

/**
 * Enables grpc-web dev tools on the specified clients
 */
export function enableDevTools(...clients: unknown[]) {
  const enableDevTools = window.__GRPCWEB_DEVTOOLS__
  if (enableDevTools) {
    enableDevTools(clients)
  }
}

/**
 * Gets an observable from a streaming call.
 *
 * @param start Factory function to start the streaming call when subscribed to.
 * @param errorOnEnd If `true`, emits an error when the call is ended by the
 *  server. Default is `false`.
 */
export function fromStreamingCall<T>(
  start: () => ClientReadableStream<T>,
  errorOnEnd?: boolean,
): Observable<T> {
  return new Observable<T>((observer) => {
    const streamingCall = start()

    streamingCall.on('data', (response) => {
      observer.next(response)
    })

    streamingCall.on('end', () => {
      if (errorOnEnd) {
        observer.error(new Error('Streaming call ended'))
      } else {
        observer.complete()
      }
    })

    streamingCall.on('error', (err) => {
      observer.error(err)
    })

    return () => {
      streamingCall.cancel()
    }
  })
}

/**
 * Patches methods on the client to include authentication info in the metadata.
 * This is needed because the gRPC-Web Chrome extension breaks with the use of
 * interceptors.
 *
 * Web dev truly has the brightest mind in the industry.
 *
 * @param client
 * @param token$
 */
export function patchGrpcClient(client: object, token$: Observable<string>) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const prototype = Object.getPrototypeOf(client)
  for (const prop in prototype) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const func: unknown = prototype[prop]
    if (typeof func === 'function' && func.length === 2) {
      // @ts-ignore
      client[prop] = async function (
        request: unknown,
        metadata: Record<string, unknown>,
      ) {
        const token = await getToken(token$)
        metadata ??= {}
        metadata.Authorization = `Bearer ${token}`
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return func.call(this, request, metadata)
      }
    }
  }

  enableDevTools(client)
}

/**
 * Subscribes to the token observable and waits for it to emit a token.
 * If it does not emit a token within a short period of time, the
 * returned promise is rejected.
 *
 * @protected
 */
async function getToken(token$: Observable<string>): Promise<string> {
  try {
    return await firstValueFrom(token$.pipe(timeout(10_000)))
  } catch (err: unknown) {
    if (err instanceof TimeoutError) {
      throw new Error('Timed out waiting for a valid token', { cause: err })
    }
    throw err
  }
}

/**
 * Used as a `catch` handler to return `null` if a `NOT_FOUND` error is thrown,
 * otherwise re-throws.
 *
 * @param err
 */
export function onRpcNotFoundReturnNull(err: unknown) {
  if (err instanceof RpcError && err.code === StatusCode.NOT_FOUND) {
    return null
  }

  throw err
}

/**
 * Checks whether the error is retryable.
 * @param err
 */
export function isRetryableRpcError(err: unknown) {
  return (
    err instanceof RpcError &&
    (err.code === StatusCode.INTERNAL ||
      err.code === StatusCode.UNAVAILABLE ||
      err.code === StatusCode.CANCELLED ||
      err.code === StatusCode.DEADLINE_EXCEEDED)
  )
}

export interface RetryOnRpcErrorOptions extends Partial<RetryOptions> {
  signal?: AbortSignal
}

/**
 * Retries a function if the error is a retryable {@link RpcError}.
 *
 * @param fn
 * @param signal
 * @param rest
 */
export async function retryOnRpcError<T>(
  fn: () => Promise<T>,
  { signal, ...rest }: RetryOnRpcErrorOptions = {},
) {
  return resolveOnAbort(
    retry(async (again) => {
      signal?.throwIfAborted()
      return fn().catch((err) => {
        if (isRetryableRpcError(err)) {
          throw again(err)
        }

        throw err
      })
    }, rest),
    signal,
  ).then(throwIfAborted)
}
